import { emitHintSignal } from '@flobi/asset-library/hints';
import { fontName } from '../../ui/fontName';
import { useProTools } from '../pro';
import type { TidyHow } from '../tidyUp';
import {
  BLEND_MODES,
  decompose,
  formatLength,
  multiply,
  parseLength,
  rotation,
  translation,
  type Color,
  type DocumentFont,
  type Effect,
  type ShaderDef,
  type LayerBlendMode,
  type LengthUnit,
  type SketchControl,
  type NodeRecord,
  type Paint,
  type TextStyle,
} from '@flobi/artwork-core';
import { useMemo, useState, type ReactNode } from 'react';
import { builtInWeights } from '../../canvas/fonts';
import { FontPicker } from '../chrome/Fonts';
import { Icon, ICON_SIZE } from '../chrome/icons';
import { Menu, useMenu } from '../../ui/menu';
import { Button, NumberField, PopUpButton, Section, SegmentedControl } from '../../ui/controls';
import { Popover, useAnchor } from '../../ui/popover';
import { tip } from '../../ui/tooltip';
import { AddEffect, EffectPage, EffectsList, SketchPage, SketchRow } from './Effects';
import { effectsOf, listedEffects, MOST_EFFECTS, previewKeyOf } from './effectList';
import type { EffectPreview } from '../useEditorCanvas';
import { PaintStack } from './Paint';
import { StrokeSection } from './StrokeSection';
import { PropertyField } from './PropertyField';
import { RepeatPanel, type RepeatActions } from './RepeatPanel';
import { ToolOptions } from './ToolOptions';
import { SelectionColors } from './SelectionColors';
import type { ColorPlace, SelectionColor } from '../selectionColors';
import type { Rail } from '../tools/types';

/**
 * Context first: object-specific settings, Layout, Appearance, Fill, Stroke, Effects.
 * Each property has one home. Several layers expose the values they can share; a single
 * anchor takes precedence over the path containing it. Combining belongs to multi-selection.
 */

export type AlignHow = 'left' | 'centre' | 'right' | 'top' | 'middle' | 'bottom' | 'spread-x' | 'spread-y';
export type ArrangeHow = 'front' | 'forward' | 'backward' | 'back';

export interface SelectionActions {
  /** Deletes what is chosen: a Lumens sketch's Remove takes its layer away. */
  deleteSelection?(): void;
  /** The middle of what a layer draws, in its own space. */
  middleOf(id: string): { x: number; y: number } | null;
  /** The box of what a layer draws, in its parent's space. */
  boxInParent(id: string): { x: number; y: number; width: number; height: number } | null;
  /** Several artboards at once: one preset size for all, laid out again, or exported. */
  sizeBoards: (ids: readonly string[], preset: string) => void;
  /** Shows (or, with null, hides) a gradient's handles on the canvas. */
  editGradient: (target: { id: string; which: 'fills' | 'strokes'; index?: number } | null) => void;
  tidyBoards: () => void;
  exportBoards: () => void;
  update(id: string, props: Record<string, unknown>): void;
  /** The same fields on several layers, as one undo step. */
  updateAll(ids: readonly string[], props: Record<string, unknown>): void;
  restyle(patch: { opacity?: number }): void;
  align(how: AlignHow): void;
  /** Lays several layers out again as a grid, masonry, column or row (useEditorCanvas tidyUp). */
  tidyUp(how: TidyHow): void;
  combine(op: 'union' | 'subtract' | 'intersect' | 'exclude'): void;
  canCombine: boolean;
  canMask: boolean;
  mask(): void;
  maskOf(id: string): NodeRecord | null;
  canReleaseMask(id: string): boolean;
  group(): void;
  ungroup(): void;
  expandBlend(): void;
  releaseBlend(): void;
  rasterize(): void;
  outlineText(): void;
  arrange(how: ArrangeHow): void;
  flip(axis: 'x' | 'y'): void;
  addEffect(type: Effect['type'], look?: Effect): void;
  /** The Effects tiles for one layer (canvas.previewEffects); absent, the chooser lists names. */
  previewEffects?(id: string, types: readonly Effect['type'][], longEdge: number, onTile?: (tile: EffectPreview) => void, signal?: AbortSignal): Promise<EffectPreview[]>;
  setEffect(id: string | readonly string[], index: number, props: Record<string, unknown>): void;
  removeEffect(id: string | readonly string[], index: number): void;
  moveEffect(id: string | readonly string[], index: number, to: number): void;
  setFont(id: string, family: string, font: DocumentFont | null): void;
  applyShader(id: string): Promise<void>;
  getShader?(id: string): ShaderDef | null;
  /** What a sketch layer runs, for its settings. */
  sketchOf?(id: string): { name: string; controls: readonly SketchControl[]; error: string | null; sources?: { id: string; name: string }[]; pictures?: { id: string; name: string }[]; chosen?: Record<string, string | null>; presets?: { name: string; seed?: number; values: Record<string, unknown> }[] } | null;
  setSketch?(id: string, props: Record<string, unknown>): void;
  /** Fills a sketch's picture slot from the canvas: outlines what may go in it, and hands back the one pressed. */
  pickLayer?(candidates: readonly string[], then: (id: string | null) => void): void;
  thumbnailOf?(id: string): Promise<string | null>;
  placeAnchor(at: { x: number; y: number }): void;
  /** Asks for another picture to put in these image layers, in place: same frame, same everything else. */
  replaceImage(ids: readonly string[]): void;
  /** How a picture sits in its frame: covering it, all of it inside, or the frame reshaped to it. */
  fitImage(ids: readonly string[], how: 'fill' | 'fit' | 'frame'): void;
  /** Every colour worn inside a group, an artboard or several layers; absent, none are listed. */
  selectionColors?: readonly SelectionColor[];
  /** Puts one colour in every place a listed colour was worn, as one undo step. */
  recolor?(places: readonly ColorPlace[], color: Color): void;
}

/** The kinds whose blend mode can be Pass Through — the ones the schema starts there (cloner-F22). */
export const PASSES_THROUGH: ReadonlySet<string> = new Set(['group', 'blend', 'cloner']);

export const BLEND_NAMES: Record<string, string> = {
  'pass-through': 'Pass Through',
  normal: 'Normal',
  multiply: 'Multiply',
  screen: 'Screen',
  overlay: 'Overlay',
  darken: 'Darken',
  lighten: 'Lighten',
  'color-dodge': 'Color Dodge',
  'color-burn': 'Color Burn',
  'hard-light': 'Hard Light',
  'soft-light': 'Soft Light',
  difference: 'Difference',
  exclusion: 'Exclusion',
  hue: 'Hue',
  saturation: 'Saturation',
  color: 'Color',
  luminosity: 'Luminosity',
};

export function SelectionInspector({
  nodes,
  units,
  actions,
  fonts,
  anchor,
  canAlign = nodes.length > 1,
  repeat,
  variationRail,
  blendRail,
}: {
  /** What is chosen, in the order it was chosen. */
  nodes: readonly NodeRecord[];
  units: LengthUnit;
  actions: SelectionActions;
  fonts: readonly DocumentFont[];
  anchor: { index: number; at: { x: number; y: number } } | null;
  canAlign?: boolean;
  repeat?: RepeatActions;
  variationRail?: Rail | null;
  blendRail?: Rail | null;
}) {
  const length = lengthField(units);

  if (anchor) {
    return (
      <Section title="Coordinates" id="context-point">
        <div className="grid grid-cols-2 gap-[6px]">
          <NumberField label="Point X" prefix="X" value={anchor.at.x} {...length} onChange={(x) => actions.placeAnchor({ x, y: anchor.at.y })} />
          <NumberField label="Point Y" prefix="Y" value={anchor.at.y} {...length} onChange={(y) => actions.placeAnchor({ x: anchor.at.x, y })} />
        </div>
      </Section>
    );
  }

  if (nodes.length > 1) return <Several nodes={nodes} units={units} actions={actions} canAlign={canAlign} />;
  const node = nodes[0];
  if (!node) return null;
  return <One node={node} units={units} actions={actions} canAlign={canAlign} fonts={fonts} repeat={repeat} variationRail={variationRail} blendRail={blendRail} />;
}

// ── several ─────────────────────────────────────────────────────────────────

function Several({ nodes, units, actions, canAlign }: {
  canAlign: boolean;
  nodes: readonly NodeRecord[];
  units: LengthUnit;
  actions: SelectionActions;
}) {
  // The effects the chosen layers share — the same list on every one — listed and edited
  // together, as one step. They were added to every layer but never listed, so they could
  // not be seen, changed or removed, and each Add stacked another unseen copy
  // (QA 2026-09-24, paint-F13). Layers whose lists differ get no list and no Add: whatever
  // was added would have nowhere to show. Locked layers take no effects, so they are not
  // asked.
  const open = nodes.filter((node) => node.locked !== true);
  const ids = open.map((node) => node.id);
  const firstEffects = open[0] ? effectsOf(open[0]) : null;
  const shared = firstEffects && open.every((node) => sameValue(effectsOf(node), firstEffects)) ? firstEffects : null;
  const sharedListed = shared && open[0] ? listedEffects(open[0]) : null;
  const editable = open.length ? open : nodes;
  const opacity = editable[0]?.opacity ?? 1;
  const mixed = editable.some(node => (node.opacity ?? 1) !== opacity);
  const pro = useProTools();
  const [page, setPage] = useState<number | null>(null);
  const opened = page !== null ? sharedListed?.effects[page] : undefined;
  if (opened && page !== null && sharedListed) return <EffectPage key={page} effect={opened} shader={opened.type === 'shader' ? actions.getShader?.(opened.shaderId) : null} units={units}
    onChange={props => actions.setEffect(ids, sharedListed.at[page]!, props)} onBack={() => setPage(null)} onRemove={() => actions.removeEffect(ids, sharedListed.at[page]!)} />;
  return (
    <fieldset disabled={!open.length} aria-label={!open.length ? 'Locked selection' : undefined} className="m-0 min-w-0 border-0 p-0">
      {pro && !nodes.some(node => ['artboard', 'slice', 'effector'].includes(node.type)) && <Section title="Combine" collapsible={false}>
        <div className="selection-geometry" role="group" aria-label="Mask and boolean operations">
          <button type="button" aria-label="Mask selection" disabled={!actions.canMask} {...tip('Mask — use the backmost object to clip the others')} onClick={actions.mask}><Icon name="mask" size={18} /></button>
          <span className="selection-geometry-divider" aria-hidden="true" />
          {([
            ['union', 'Union', 'Merge the selected shapes'],
            ['subtract', 'Subtract', 'Cut the front shapes out of the back shape'],
            ['intersect', 'Intersect', 'Keep only the overlapping area'],
            ['exclude', 'Exclude', 'Remove the overlapping area'],
          ] as const).map(([op, label, hint]) => <button key={op} type="button" aria-label={label} disabled={!actions.canCombine} {...tip(actions.canCombine ? `${label} — ${hint}` : `${label} — select two unlocked, closed vector shapes`)} onClick={() => { actions.combine(op); emitHintSignal('shapes.combined', { intersect: op === 'intersect' }); }}><Icon name={op} size={18} /></button>)}
        </div>
      </Section>}
      <PositionSection nodes={nodes} units={units} actions={actions} canAlign={canAlign} />
      <Section title="Appearance">
        <div className="prop-row">
          <PropertyField label="Opacity"><NumberField label="Opacity" prefix={<Icon name="prop-opacity" size={14} />} value={Math.round(opacity * 100)} format={mixed ? () => 'Mixed' : undefined} min={0} max={100} suffix="%" onChange={(value) => actions.restyle({ opacity: value / 100 })} /></PropertyField>
        </div>
      </Section>
      <SelectionColorsOf actions={actions} />
      {shared && sharedListed ? (
        <Section title="Effects" revealKey={sharedListed.effects.length} emptyAction={!sharedListed.effects.length ? <AddEffect showLabel onShader={actions.applyShader} onAdd={actions.addEffect} /> : undefined} accessory={<AddEffect onShader={actions.applyShader} onAdd={actions.addEffect} disabled={shared.length >= MOST_EFFECTS} />}>
          {sharedListed.effects.length ? (
            <EffectsList
              key={ids.join(',')}
              getShader={actions.getShader}
              effects={sharedListed.effects}
              units={units}
              onChange={(index, props) => actions.setEffect(ids, sharedListed.at[index]!, props)}
              onRemove={(index) => actions.removeEffect(ids, sharedListed.at[index]!)}
              onReorder={(index, to) => actions.moveEffect(ids, sharedListed.at[index]!, sharedListed.at[to]!)}
              onOpen={setPage}
            />
          ) : null}
        </Section>
      ) : null}
    </fieldset>
  );
}

// ── one ─────────────────────────────────────────────────────────────────────

export type Shaped = NodeRecord & {
  width?: number;
  height?: number;
  length?: number;
  fills?: Paint[];
  strokes?: Paint[];
  strokeStyle?: { weight?: number; align?: 'center' | 'inside' | 'outside'; cap?: 'butt' | 'round' | 'square'; join?: 'miter' | 'round' | 'bevel'; dashes?: number[]; dashOffset?: number; miterLimit?: number };
  radii?: number[];
  sides?: number;
  points?: number;
  innerRatio?: number;
  cornerRadius?: number;
  cornerSmoothing?: number;
  arc?: { start: number; sweep: number; inner: number };
  blendMode?: LayerBlendMode;
  style?: Partial<TextStyle>;
  verticalAlign?: 'top' | 'middle' | 'bottom';
  align?: 'start' | 'center' | 'end' | 'justify';
  effects?: Effect[];
  path?: { subpaths?: { closed?: boolean; points?: unknown[] }[] };
};

function One({ node, units, actions, fonts, repeat, variationRail, blendRail, canAlign }: {
  canAlign: boolean;
  node: NodeRecord;
  units: LengthUnit;
  actions: SelectionActions;
  fonts: readonly DocumentFont[];
  repeat?: RepeatActions;
  variationRail?: Rail | null;
  blendRail?: Rail | null;
}) {
  const n = node as Shaped;
  const id = node.id;
  const set = (props: Record<string, unknown>) => actions.update(id, props);
  // A combined shape is a shape: it is drawn with its own fill and stroke, so it gets
  // them here (QA 2026-09-24, paint-F1). Groups, blends and cloners hold paint in their layers.
  const container = ['group', 'blend', 'cloner'].includes(node.type);
  const locked = node.locked === true;
  const mask = actions.maskOf(id);
  // An effect opened from the list takes the panel until Back (Tarek, 2 Nov: "drill into the panel").
  const [page, setPage] = useState<number | null>(null);
  const fx = listedEffects(n);
  const opened = page !== null ? fx.effects[page] : undefined;
  // A Lumens sketch is listed with the effects and opens the same way (page -1).
  const sketch = node.type === 'image' && node.sketch ? actions.sketchOf?.(node.id) ?? null : null;
  if (sketch && page === -1 && node.type === 'image') return <fieldset disabled={locked} className="m-0 min-w-0 border-0 p-0">
    <SketchPage node={node} name={sketch.name || 'Sketch'} controls={sketch.controls} error={sketch.error} units={units} sources={sketch.sources} pictures={sketch.pictures} chosen={sketch.chosen} presets={sketch.presets}
      onPick={actions.pickLayer} thumbnailOf={actions.thumbnailOf}
      onChange={props => (props.visible !== undefined ? actions.update(id, props) : actions.setSketch?.(id, props))} onBack={() => setPage(null)} onRemove={() => actions.deleteSelection?.()} />
  </fieldset>;
  if (opened && page !== null) return <fieldset disabled={locked} className="m-0 min-w-0 border-0 p-0">
    <EffectPage key={page} effect={opened} shader={opened.type === 'shader' ? actions.getShader?.(opened.shaderId) : null} units={units}
      onChange={props => actions.setEffect(id, fx.at[page]!, props)} onBack={() => setPage(null)} onRemove={() => actions.removeEffect(id, fx.at[page]!)} />
  </fieldset>;
  return (
    <fieldset disabled={locked} aria-label={locked ? 'Locked' : undefined} className={`m-0 min-w-0 border-0 p-0 ${locked ? 'opacity-50' : ''}`}>
      {node.type === 'text' && <TextSettings node={n} set={set} fonts={fonts} onFont={(family, font) => actions.setFont(id, family, font)} />}
      {node.type === 'image' && <ImageSection node={node} actions={actions} />}
      <ShapeSettings node={n} set={set} units={units} />
      {node.type === 'cloner' && repeat && <Section title="Cloner" id="context-repeat"><RepeatPanel cloner={node} actions={repeat} embedded /></Section>}
      {node.type === 'blend' && <BlendSettings node={node} set={set} units={units} rail={blendRail} />}
      {node.type === 'effector' && variationRail ? <Section title="Variation" id="context-variation"><ToolOptions rail={variationRail} sectioned={false} /></Section> : null}
      {mask && <Section title="Mask" collapsible={false}>
        <p className="m-0 text-[11px] text-label-2">{mask.id === id ? 'Clips the layers above it in this group.' : `Clipped by ${mask.name || mask.type}.`}</p>
        <Button size="small" icon="release" disabled={!actions.canReleaseMask(mask.id)} onClick={() => actions.update(mask.id, { isMask: false })}>Release mask</Button>
      </Section>}
      <PositionSection nodes={[node]} units={units} actions={actions} canAlign={canAlign} />
      <LayoutSection node={n} set={set} actions={actions} units={units} />
      <AppearanceSection node={n} set={set} />
      {container && <SelectionColorsOf actions={actions} />}
      {node.type !== 'slice' && <PaintSections node={n} set={set} actions={actions} units={units} container={container} onOpenEffect={setPage}
        lead={sketch ? <SketchRow name={sketch.name || 'Sketch'} on={node.visible !== false} onOpen={() => setPage(-1)} onToggle={() => actions.update(id, { visible: node.visible === false })} onRemove={() => actions.deleteSelection?.()} /> : undefined} />}
    </fieldset>
  );
}

/**
 * A picture's own section: swap it for another, and say how it sits in its frame. The
 * frame is the layer — its size, place, animation, effects — so a swapped picture arrives
 * wearing all of it; that is what makes a file a template for somebody else's photographs.
 */
function ImageSection({ node, actions }: { node: Extract<NodeRecord, { type: 'image' }>; actions: SelectionActions }) {
  return (
    <Section title="Image" id="context-image">
      <Button size="small" icon="pictures" onClick={() => actions.replaceImage([node.id])}>Replace Image…</Button>
      <SegmentedControl
        size="small"
        label="In its frame"
        value={node.fit === 'fit' ? 'fit' : 'fill'}
        onChange={(how) => actions.fitImage([node.id], how)}
        segments={[
          { value: 'fill', label: 'Fill', tip: 'Covers the frame; what does not fit is cropped' },
          { value: 'fit', label: 'Fit', tip: 'All of the picture, inside the frame' },
        ]}
      />
      <Button size="small" variant="borderless" onClick={() => actions.fitImage([node.id], 'frame')} {...tip('Reshapes the frame to the picture’s own proportions')}>Match Picture’s Shape</Button>
    </Section>
  );
}

export function BlendSettings({ node, set, units, rail, bare = false }: { bare?: boolean; node: Extract<NodeRecord, { type: 'blend' }>; set: (props: Record<string, unknown>) => void; units: LengthUnit; rail?: Rail | null }) {
  // Reuse the tool's commands: a locked endpoint also locks its blend's settings.
  const modes = rail?.top?.kind === 'buttons' ? rail.top.items : null;
  const locked = !!modes?.length && modes.every(item => item.disabled);
  const quantity = rail?.middle?.kind === 'slider' ? rail.middle : null;
  return <SettingsSection bare={bare} title="Blend" id="context-blend">
    <SegmentedControl label="Blend spacing" value={node.spacing} onChange={spacing => {
      if (modes) { const choice = modes.find(item => item.id === spacing); if (choice && !choice.disabled) choice.run({ more: false }); }
      else set({ spacing });
    }} segments={[
      { value: 'smooth', label: 'Smooth' }, { value: 'steps', label: 'Steps' }, { value: 'distance', label: 'Distance' },
    ].map(item => ({ ...item, disabled: locked }))} />
    {node.spacing === 'steps' && <NumberField label="Blend steps" prefix="Steps" prefixWidth={48} value={node.steps} min={1} max={64} decimals={0} disabled={locked} onChange={steps => quantity ? quantity.onChange(steps) : set({ steps })} />}
    {node.spacing === 'distance' && <NumberField label="Blend distance" prefix="Distance" prefixWidth={58} value={node.distance} min={2} max={200} disabled={locked} {...lengthField(units)} onChange={distance => quantity ? quantity.onChange(distance) : set({ distance })} />}
  </SettingsSection>;
}

export function SelectionColorsOf({ actions }: { actions: SelectionActions }) {
  return actions.selectionColors && actions.recolor ? <SelectionColors colors={actions.selectionColors} onChange={actions.recolor} /> : null;
}

function AppearanceSection({ node, set }: { node: Shaped; set: (props: Record<string, unknown>) => void }) {
  const pro = useProTools();
  return <Section title="Appearance">
    <div className="prop-row">
      <PropertyField label="Opacity"><NumberField label="Opacity" prefix={<Icon name="prop-opacity" size={14} />} value={Math.round((node.opacity ?? 1) * 100)} suffix="%" min={0} max={100} onChange={value => set({ opacity: value / 100 })} /></PropertyField>
      {pro && node.type !== 'slice' && <PropertyField label="Blend mode"><PopUpButton label="Blend mode" value={node.blendMode ?? 'normal'} onChange={blendMode => set({ blendMode })} options={(PASSES_THROUGH.has(node.type) ? ['pass-through', ...BLEND_MODES] : BLEND_MODES).map(mode => [mode, BLEND_NAMES[mode] ?? mode] as const)} /></PropertyField>}
    </div>
  </Section>;
}

function PaintSections({ node, set, actions, units, container, onOpenEffect, lead }: { node: Shaped; set: (props: Record<string, unknown>) => void; actions: SelectionActions; units: LengthUnit; container: boolean; onOpenEffect: (index: number) => void; /** A row listed before the effects: a Lumens sketch drawing the layer. */ lead?: ReactNode }) {
  const paintable = node.type !== 'image' && !container;
  const listed = listedEffects(node);
  const tilesFor = (id: string) => actions.previewEffects ? (types: readonly Effect['type'][], longEdge: number, onTile?: (tile: EffectPreview) => void, signal?: AbortSignal) => actions.previewEffects!(id, types, longEdge, onTile, signal) : undefined;
  const tilesKey = useMemo(() => previewKeyOf(node), [node]);
  return (
    <>
      {paintable ? (
        <PaintStack key={node.id} label="Fill" paints={(node.type === 'text' ? node.style?.fills : node.fills) ?? []}
          onChange={fills => set(node.type === 'text' ? { style: { ...node.style, fills } } : { fills })}
          onPicking={(index) => actions.editGradient(index === null ? null : { id: node.id, which: 'fills', index })} />
      ) : null}
      {node.type !== 'image' && !container ? <StrokeSection node={node} set={set} units={units} onPicking={index => actions.editGradient(index === null ? null : { id: node.id, which: 'strokes', index })} /> : null}
      {/* The list holds 32 at most; past that Add was refused without a word (QA 2026-09-24, paint-F22). */}
      <Section title="Effects" revealKey={listed.effects.length + (lead ? 1 : 0)} emptyAction={!listed.effects.length && !lead ? <AddEffect showLabel onShader={actions.applyShader} onAdd={actions.addEffect} preview={tilesFor(node.id)} previewKey={tilesKey} /> : undefined} accessory={<AddEffect onShader={actions.applyShader} onAdd={actions.addEffect} disabled={effectsOf(node).length >= MOST_EFFECTS} preview={tilesFor(node.id)} previewKey={tilesKey} />}>
        {lead}
        {listed.effects.length ? (
          <EffectsList
            key={node.id}
            getShader={actions.getShader}
            effects={listed.effects}
            units={units}
            onChange={(index, props) => actions.setEffect(node.id, listed.at[index]!, props)}
            onRemove={(index) => actions.removeEffect(node.id, listed.at[index]!)}
            onReorder={(index, to) => actions.moveEffect(node.id, listed.at[index]!, listed.at[to]!)}
            onOpen={onOpenEffect}
          />
        ) : null}
      </Section>
    </>
  );
}

/**
 * One layer's effects for a panel that is not this inspector: an artboard's, under its size and
 * background. The same list and Add as a shape's; an opened effect takes the panel (`children` and
 * `after` are what show above and below the list while none is).
 */
export function LayerEffects({ node, actions, units, children, after }: { node: NodeRecord; actions: SelectionActions; units: LengthUnit; children?: ReactNode; after?: ReactNode }) {
  const [page, setPage] = useState<number | null>(null);
  const listed = listedEffects(node);
  const opened = page !== null ? listed.effects[page] : undefined;
  if (opened && page !== null) return <EffectPage key={page} effect={opened} shader={opened.type === 'shader' ? actions.getShader?.(opened.shaderId) : null} units={units}
    onChange={props => actions.setEffect(node.id, listed.at[page]!, props)} onBack={() => setPage(null)} onRemove={() => actions.removeEffect(node.id, listed.at[page]!)} />;
  return <>
    {children}
    <Section title="Effects" revealKey={listed.effects.length} emptyAction={!listed.effects.length ? <AddEffect showLabel onShader={actions.applyShader} onAdd={actions.addEffect} /> : undefined} accessory={<AddEffect onShader={actions.applyShader} onAdd={actions.addEffect} disabled={effectsOf(node).length >= MOST_EFFECTS} />}>
      {listed.effects.length ? (
        <EffectsList
          key={node.id}
          getShader={actions.getShader}
          effects={listed.effects}
          units={units}
          onChange={(index, props) => actions.setEffect(node.id, listed.at[index]!, props)}
          onRemove={(index) => actions.removeEffect(node.id, listed.at[index]!)}
          onReorder={(index, to) => actions.moveEffect(node.id, listed.at[index]!, listed.at[to]!)}
          onOpen={setPage}
        />
      ) : null}
    </Section>
    {after}
  </>;
}

const ALIGNMENTS = [
  ['left', 'Align left', 'align-left'], ['centre', 'Align centers', 'align-center'], ['right', 'Align right', 'align-right'],
  ['top', 'Align top', 'align-top'], ['middle', 'Align middles', 'align-middle'], ['bottom', 'Align bottom', 'align-bottom'],
] as const;

/** Shared by objects, selections and explicitly selected artboards. */
export function PositionSection({ nodes, actions, units, canAlign }: {
  nodes: readonly NodeRecord[];
  actions: SelectionActions;
  units: LengthUnit;
  canAlign: boolean;
}) {
  const order = useMenu();
  const editable = nodes.filter(node => !node.locked);
  const unlocked = editable.length > 0;
  const boards = nodes.every(node => node.type === 'artboard');
  const one = nodes.length === 1 ? nodes[0] : undefined;
  const alignEnabled = canAlign && unlocked && (nodes.length === 1 ? !boards : editable.length > 1);
  const pro = useProTools();
  const flipButtons = !boards && <>
    <button type="button" aria-label="Flip horizontally" disabled={!unlocked} {...tip('Flip horizontally')} onClick={() => actions.flip('x')}><Icon name="flip-h" size={16} /></button>
    <button type="button" aria-label="Flip vertically" disabled={!unlocked} {...tip('Flip vertically')} onClick={() => actions.flip('y')}><Icon name="flip-v" size={16} /></button>
  </>;
  // Figma's grammar (Tarek, 2 Nov): each group under its own small label, the buttons in
  // wells of three, the fields two to a row with a gutter for one icon.
  return <Section title="Position" id="context-position" accessory={!pro ? undefined : <>
    <button type="button" ref={order.ref} aria-label="Layer order" aria-haspopup="menu" aria-expanded={order.open} disabled={!unlocked} className="position-order" {...tip('Layer order')} onClick={order.toggle}><Icon name="more" size={16} /></button>
    <Menu label="Layer order" open={order.open} anchor={order.anchor} onClose={order.close} placement="below" items={(['front', 'forward', 'backward', 'back'] as const).map((how, i) => ({
      id: how, label: ['Bring to front', 'Bring forward', 'Send backward', 'Send to back'][i]!, icon: i < 2 ? 'bring-forward' : 'send-backward', disabled: !unlocked,
      onSelect: () => { actions.arrange(how); emitHintSignal('layers.arranged', { viaMenu: true }); },
    }))} />
  </>}>
    <PropertyField label="Alignment">
      <div className="prop-row" role="group" aria-label="Alignment">
        {[ALIGNMENTS.slice(0, 3), ALIGNMENTS.slice(3)].map((three, at) => <div key={at} className="prop-buttons">
          {three.map(([how, label, icon]) => <button key={how} type="button" aria-label={label} disabled={!alignEnabled} {...tip(alignEnabled ? label : !unlocked ? 'Unlock a layer to align it' : nodes.length === 1 ? 'Select an object on an artboard, or multiple objects, to align' : 'Select at least two unlocked objects to align')} onClick={() => { actions.align(how); emitHintSignal('layers.aligned', { count: nodes.length, spread: false }); }}><Icon name={icon} size={16} /></button>)}
        </div>)}
      </div>
    </PropertyField>
    {nodes.length >= 3 && <PropertyField label="Distribution">
      <div className="prop-row" role="group" aria-label="Distribution">
        <div className="prop-buttons">
          {([['spread-x', 'Distribute horizontally', 'distribute-h'], ['spread-y', 'Distribute vertically', 'distribute-v']] as const).map(([how, label, icon]) => <button key={how} type="button" aria-label={label} disabled={!canAlign || editable.length < 3} {...tip(label)} onClick={() => { actions.align(how); emitHintSignal('layers.distributed'); }}><Icon name={icon} size={16} /></button>)}
        </div>
      </div>
    </PropertyField>}
    {one ? <fieldset disabled={!unlocked} className="m-0 grid min-w-0 gap-[8px] border-0 p-0"><PositionFields node={one as Shaped} set={props => actions.update(one.id, props)} actions={actions} units={units} flip={flipButtons} /></fieldset>
      : flipButtons ? <PropertyField label="Flip"><div className="prop-row"><div className="prop-buttons" role="group" aria-label="Flip">{flipButtons}</div></div></PropertyField> : null}
  </Section>;
}

function PositionFields({ node, set, actions, units, flip }: { node: Shaped; set: (props: Record<string, unknown>) => void; actions: SelectionActions; units: LengthUnit; flip: ReactNode }) {
  const t = node.transform;
  const length = lengthField(units);
  const angle = decompose(t).rotation;

  /**
   * Turns the layer to an angle about its own middle, so typing an angle spins it where
   * it stands rather than swinging it round its corner.
   */
  // Only for layers with no width/height/length of their own.
  const box = node.width === undefined && node.length === undefined ? actions.boxInParent(node.id) : null;
  // A flipped layer's origin is its far edge, so X showed the right edge: its X and Y read
  // the box it draws, as a group's do. And a turn made in its own (mirrored) space runs the
  // other way, so the step is reversed there to arrive at the angle typed
  // (QA 2026-09-24, inspector-numbers-F16).
  const mirrored = t[0] * t[3] - t[1] * t[2] < 0;
  const place = box ?? (mirrored ? actions.boxInParent(node.id) : null);
  const turnTo = (degrees: number) => {
    // About the middle of what it draws. Groups and paths have no width or height, so
    // width/2, height/2 was their origin — a corner — and they swung round it
    // (QA 2026-09-24, sel-F9).
    const middle = actions.middleOf(node.id) ?? { x: (node.width ?? 0) / 2, y: (node.height ?? 0) / 2 };
    const cx = middle.x;
    const cy = middle.y;
    const about = multiply(multiply(translation(cx, cy), rotation(mirrored ? angle - degrees : degrees - angle)), translation(-cx, -cy));
    set({ transform: multiply(t, about) });
  };
  // A quarter turn, kept inside ±180 so the field reads as it would typed.
  const quarter = () => turnTo(((angle + 90 + 180) % 360 + 360) % 360 - 180);

  return <>
    <PropertyField label="Position">
      <div className="prop-row">
        {/* A layer with no size of its own (a group, a path) shows the box it draws, and a
            typed X or Y moves it by the difference. It showed its origin — X 0, Y 0 for
            every new group — and typing moved it by the typed amount
            (QA 2026-09-24, inspector-numbers-N1). */}
        <NumberField label="X" prefix="X" value={place ? place.x : t[4]} {...length} onChange={(x) => set({ transform: [t[0], t[1], t[2], t[3], place ? t[4] + (x - place.x) : x, t[5]] })} />
        <NumberField label="Y" prefix="Y" value={place ? place.y : t[5]} {...length} onChange={(y) => set({ transform: [t[0], t[1], t[2], t[3], t[4], place ? t[5] + (y - place.y) : y] })} />
      </div>
    </PropertyField>
    <PropertyField label="Rotation">
      <div className="prop-row">
        <NumberField label="Rotation" prefix={<Icon name="angle" size={14} />} value={Math.round(angle * 10) / 10} suffix="°" decimals={1} onChange={turnTo} />
        {flip ? <div className="prop-buttons" role="group" aria-label="Rotate and flip">
          <button type="button" aria-label="Rotate 90°" {...tip('Rotate 90°')} onClick={quarter}><Icon name="prop-rotate" size={16} /></button>
          {flip}
        </div> : null}
      </div>
    </PropertyField>
  </>;
}

function LayoutSection({ node, set, actions, units }: { node: Shaped; set: (props: Record<string, unknown>) => void; actions: SelectionActions; units: LengthUnit }) {
  const length = lengthField(units);
  const box = node.width === undefined && node.length === undefined ? actions.boxInParent(node.id) : null;
  const pro = useProTools();
  // Open by default, as Figma's is: a layer's size is read as often as its place. A new id,
  // so the fold every file used to start with is not remembered against it.
  return <Section title="Layout" id="context-size">
        {node.type === 'text' && pro && <PropertyField label="Resizing"><SegmentedControl label="Text resizing" value={node.sizing ?? 'auto-width'} onChange={sizing => set({ sizing })} segments={[{ value: 'auto-width', label: 'Auto width' }, { value: 'auto-height', label: 'Auto height' }, { value: 'fixed', label: 'Fixed' }]} /></PropertyField>}
        <PropertyField label="Dimensions">
        <div className="prop-row">
          {box ? <NumberField label="Width" prefix="W" value={box.width} {...length} disabled onChange={() => {}} /> : null}
          {box ? <NumberField label="Height" prefix="H" value={box.height} {...length} disabled onChange={() => {}} /> : null}
          {/* A text that hugs its line ignores a width, and its next measure wrote the old
              one back: a typed W wraps it at that width instead, and a typed H fixes the
              box, as dragging its handles already does (QA 2026-09-24, text-F21). */}
          {node.width !== undefined ? <NumberField label="Width" prefix="W" value={node.width} min={1} {...length} onChange={(width) => set(node.type === 'text' && node.sizing === 'auto-width' ? { width, sizing: 'auto-height' } : { width })} /> : null}
          {node.height !== undefined ? <NumberField label="Height" prefix="H" value={node.height} min={1} {...length} onChange={(height) => set(node.type === 'text' && node.sizing !== 'fixed' ? { height, sizing: 'fixed' } : { height })} /> : null}
          {node.length !== undefined ? <NumberField label="Length" prefix="L" value={node.length} min={0} {...length} onChange={(value) => set({ length: value })} /> : null}
        </div>
        </PropertyField>
  </Section>;
}

/** Offered only once there is a corner to smooth: on a sharp one it would change nothing. */
function SmoothingSlider({ node, set }: { node: Shaped; set: (props: Record<string, unknown>) => void }) {
  return <LabelledSlider label="Smoothing" value={Math.round((node.cornerSmoothing ?? 0) * 100)} min={0} max={100} suffix="%" onChange={(value) => set({ cornerSmoothing: value / 100 })} />;
}

/** The shape's own geometry, where it has any: corners, sides, points, a pie's arc. */
export function ShapeSettings({ node, set, units, bare = false }: { bare?: boolean; node: Shaped; set: (props: Record<string, unknown>) => void; units: LengthUnit }) {
  const length = lengthField(units);
  if (node.type === 'rect') {
    const most = Math.max(0, Math.min(node.width ?? 0, node.height ?? 0) / 2);
    // The radius the corners are drawn with: a stored one past half the shorter side is
    // scaled down when drawn, so that is the number to show — it sat past the end of its own
    // slider otherwise (QA 2026-09-24, shapes-F17).
    const radius = Math.min(node.radii?.[0] ?? 0, most);
    return (
      <SettingsSection bare={bare} title="Shape" id="context-rect">
        <div className="prop-row">
          <LabelledSlider label="Radius" value={radius} min={0} max={most} onChange={(r) => set({ radii: [r, r, r, r] })} field={length} />
          {node.radii?.some((r) => r > 0) ? <SmoothingSlider node={node} set={set} /> : null}
        </div>
      </SettingsSection>
    );
  }
  if (node.type === 'polygon') {
    return (
      <SettingsSection bare={bare} title="Shape" id="context-polygon">
        <div className="prop-row">
          <LabelledSlider label="Sides" value={node.sides ?? 6} min={3} max={24} onChange={(sides) => set({ sides: Math.round(sides) })} />
          <LabelledSlider label="Corner radius" value={node.cornerRadius ?? 0} min={0} max={Math.max(1, (node.width ?? 100) / 2)} onChange={(cornerRadius) => set({ cornerRadius })} field={length} />
        </div>
        {node.cornerRadius ? <div className="prop-row"><SmoothingSlider node={node} set={set} /></div> : null}
      </SettingsSection>
    );
  }
  if (node.type === 'star') {
    return (
      <SettingsSection bare={bare} title="Shape" id="context-star">
        <div className="prop-row">
          <LabelledSlider label="Points" value={node.points ?? 5} min={3} max={24} onChange={(points) => set({ points: Math.round(points) })} />
          <LabelledSlider label="Inner radius" value={Math.round((node.innerRatio ?? 0.382) * 100)} min={5} max={95} suffix="%" onChange={(value) => set({ innerRatio: value / 100 })} />
        </div>
        <div className="prop-row">
          <LabelledSlider label="Corner radius" value={node.cornerRadius ?? 0} min={0} max={Math.max(1, (node.width ?? 100) / 4)} onChange={(cornerRadius) => set({ cornerRadius })} field={length} />
          {node.cornerRadius ? <SmoothingSlider node={node} set={set} /> : null}
        </div>
      </SettingsSection>
    );
  }
  if (node.type === 'ellipse') {
    const arc = node.arc ?? { start: 0, sweep: 360, inner: 0 };
    return (
      <SettingsSection bare={bare} title="Shape" id="context-ellipse">
        <div className="prop-row">
          <LabelledSlider label="Sweep" value={arc.sweep} min={1} max={360} suffix="°" onChange={(sweep) => set({ arc: { ...arc, sweep } })} />
          <LabelledSlider label="Start" value={arc.start} min={-180} max={180} suffix="°" onChange={(start) => set({ arc: { ...arc, start } })} />
        </div>
        <div className="prop-row">
          <LabelledSlider label="Hole" value={Math.round(arc.inner * 100)} min={0} max={95} suffix="%" onChange={(value) => set({ arc: { ...arc, inner: value / 100 } })} />
        </div>
      </SettingsSection>
    );
  }
  return null;
}

export function TextSettings({
  node,
  set,
  fonts,
  onFont,
  bare = false,
  part,
}: {
  bare?: boolean;
  part?: 'font' | 'spacing';
  node: Shaped;
  set: (props: Record<string, unknown>) => void;
  fonts: readonly DocumentFont[];
  onFont: (family: string, font: DocumentFont | null) => void;
}) {
  const style = node.style ?? {};
  const [anchor, setAnchor] = useAnchor<HTMLButtonElement>();
  const [picking, setPicking] = useState(false);
  const family: string = style.fontFamily ?? 'Inter';
  // A bundled family offers the weights it ships with, not Thin to Black (text-F14). The
  // weight a text already carries stays listed, so the menu never reads blank.
  const inFile = (fonts.find((font) => font.family === family)?.faces ?? []).map((face) => face.weight);
  const offered = inFile.length ? inFile : (builtInWeights(family) ?? [100, 200, 300, 400, 500, 600, 700, 800, 900]);
  const weights = [...offered, style.fontWeight ?? 400].filter((w, i, all) => all.indexOf(w) === i).sort((a, b) => a - b);
  const setStyle = (patch: Partial<TextStyle>) => set({ style: { ...style, ...patch } });
  const lineHeight = style.lineHeight;
  const spacing = style.letterSpacing;
  const linePx = lineHeight && lineHeight !== 'auto' ? (lineHeight.unit === 'percent' ? (style.fontSize ?? 16) * lineHeight.value / 100 : lineHeight.value) : (style.fontSize ?? 16) * 1.2;
  return (
    <SettingsSection bare={bare} title="Typography" id="context-typography">
      {part !== 'spacing' && <>
      <div className="prop-row">
      <button
        ref={setAnchor}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={picking}
        aria-label={`Font: ${fontName(family, fonts)}`}
        onClick={() => setPicking((was) => !was)}
        className="font-family-button prop-span flex h-[28px] items-center gap-[8px] rounded-[8px] bg-fill-1 px-[9px] text-left transition-colors hover:bg-fill-selected"
      >
        <span className="min-w-0 flex-1 truncate text-[13px] text-label" style={{ fontFamily: `'${family}', var(--font-sans)` }}>
          {fontName(family, fonts)}
        </span>
        <span className="text-label-2">
          <Icon name="chevron-up-down" size={ICON_SIZE.indicator} />
        </span>
      </button>
      </div>
      <Popover open={picking} anchor={anchor} onClose={() => setPicking(false)} placement="left" align="start" width={260} label="Fonts">
        <FontPicker value={family} inDocument={fonts} onChoose={onFont} onClose={() => setPicking(false)} />
      </Popover>
      <div className="prop-row">
        <PopUpButton
          label="Weight"
          value={String(style.fontWeight ?? 400)}
          onChange={(value) => set({ style: { ...style, fontWeight: Number(value) } })}
          options={weights.map((w) => [String(w), WEIGHT_NAMES[w] ?? String(w)] as const)}
        />
        <NumberField label="Size" prefix={<Icon name="prop-font-size" size={14} />} value={style.fontSize ?? 16} min={1} max={10000} step={1} decimals={1} onChange={(fontSize) => set({ style: { ...style, fontSize } })} />
      </div>
      </>}
      {part !== 'font' && <div className="prop-row">
        <PropertyField label="Line height"><NumberField label="Line height" prefix={<Icon name="prop-line-height" size={14} />} suffix="px" value={linePx} min={0} max={100000} format={value => !lineHeight || lineHeight === 'auto' ? 'Auto' : String(Math.round(value * 10) / 10)} onChange={value => setStyle({ lineHeight: { unit: 'px', value } })} /></PropertyField>
        <PropertyField label="Letter spacing"><NumberField label="Letter spacing" prefix={<Icon name="prop-letter-spacing" size={14} />} suffix={spacing?.unit === 'px' ? 'px' : '%'} value={spacing?.value ?? 0} min={-100000} max={100000} step={0.1} onChange={value => setStyle({ letterSpacing: { unit: spacing?.unit ?? 'percent', value } })} /></PropertyField>
      </div>}
      {/* Across and down side by side under one label, as Figma has them. */}
      {part !== 'font' && <PropertyField label="Alignment"><div className="prop-row">
        {!part && <SegmentedControl label="Text alignment" value={node.align ?? 'start'} onChange={align => set({ align })} segments={[
          { value: 'start', icon: 'prop-text-left', tip: 'Align text left' },
          { value: 'center', icon: 'prop-text-center', tip: 'Align text center' },
          { value: 'end', icon: 'prop-text-right', tip: 'Align text right' },
          { value: 'justify', icon: 'prop-text-justify', tip: 'Justify' },
        ]} />}
        <SegmentedControl label="Vertical text alignment" value={node.verticalAlign ?? 'top'} onChange={verticalAlign => set({ verticalAlign })} segments={[
          { value: 'top', icon: 'prop-align-top', tip: 'Align text top' },
          { value: 'middle', icon: 'prop-align-middle', tip: 'Align text middle' },
          { value: 'bottom', icon: 'prop-align-bottom', tip: 'Align text bottom' },
        ]} />
      </div></PropertyField>}
      {part !== 'font' && <button className="properties-reset" onClick={() => setStyle({ lineHeight: 'auto', letterSpacing: { unit: 'percent', value: 0 } })}>Reset spacing</button>}
    </SettingsSection>
  );
}

const WEIGHT_NAMES: Record<number, string> = {
  100: 'Thin',
  200: 'Extra Light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semibold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
};

// ── pieces ──────────────────────────────────────────────────────────────────

/**
 * A shape's number, labelled above its field like every other in the panel. The sliders went
 * (Tarek, 2 Nov: "remove the sliders"): the field still scrubs, takes arrows and clamps.
 */
function LabelledSlider({
  label,
  value,
  min,
  max,
  suffix,
  onChange,
  field,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  suffix?: string;
  onChange: (value: number) => void;
  field?: ReturnType<typeof lengthField>;
}) {
  return (
    <PropertyField label={label}>
      <NumberField label={label} value={value} min={min} max={max} suffix={field ? field.suffix : suffix} decimals={field ? 2 : 0} {...(field ? { format: field.format, parse: field.parse } : {})} onChange={onChange} />
    </PropertyField>
  );
}

/** Whether two stored values are the same, whatever order their keys were written in. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a).filter((k) => (a as Record<string, unknown>)[k] !== undefined);
  const kb = Object.keys(b).filter((k) => (b as Record<string, unknown>)[k] !== undefined);
  return ka.length === kb.length && ka.every((k) => sameValue((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/** A length field in the document's own unit: shown, typed and scrubbed in it. */
function lengthField(units: LengthUnit) {
  return {
    format: (value: number) => formatLength(value, units, { bare: true }),
    parse: (text: string) => parseLength(text, units),
    suffix: units === 'ft-in' ? undefined : units,
    decimals: 2,
  };
}

/** The same controls work in Properties and in a toolbar popover. */
function SettingsSection({ bare, title, id, children }: { bare: boolean; title: string; id: string; children: ReactNode }) {
  return bare ? <div className="grid gap-[10px]">{children}</div> : <Section title={title} id={id}>{children}</Section>;
}
